<template>
  <div class="otp-2fa-content">
    <!-- OTP Input Boxes -->
    <div class="otp-input-group">
      <input
        v-for="(digit, index) in otp"
        :key="index"
        :ref="`otpInput${index}`"
        v-model="otp[index]"
        type="text"
        class="otp-input"
        inputmode="numeric"
        maxlength="1"
        placeholder="-"
        @input="handleInput(index, $event)"
        @keydown="handleKeydown(index, $event)"
        @paste="handlePaste"
      />
    </div>

    <!-- Error Message -->
    <v-alert
      v-if="errorMessage"
      type="error"
      dismissible
      class="mt-4"
      @input="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- Verify Button -->
    <v-btn
      block
      large
      color="primary"
      class="mt-6"
      :disabled="otp.join('').length !== 6 || isVerifying"
      :loading="isVerifying"
      @click="verify2FA"
    >
      ยืนยันรหัส 2FA
    </v-btn>

    <!-- Resend OTP Section -->
    <div class="resend-section mt-6">
      <div v-if="resendCountdown > 0" class="countdown-info">
        <span class="countdown-text">
          ขอรหัส OTP ใหม่ได้ใน
          <strong>{{ resendCountdown }}</strong>
          วินาที
        </span>
      </div>

      <v-btn
        v-else
        text
        color="primary"
        :loading="isResending"
        @click="resend2FA"
      >
        {{ hasRequestedOtp ? 'ขอรหัส OTP ใหม่' : 'ขอรหัส OTP' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'OTP2FAContent',

  props: {
    phone: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      otp: ['', '', '', '', '', ''],
      isVerifying: false,
      isResending: false,
      errorMessage: '',
      resendCountdown: 0,
      resendTimer: null,
      hasRequestedOtp: false,
    }
  },

  computed: {
    isResendCooldown() {
      return this.resendCountdown > 0
    },
  },

  mounted() {
    // ไม่เริ่มนับถอยหลังตอน mount เพราะ 2FA modal ถูกเปิดจาก 401
    // "2FA verification required" ซึ่ง backend ยังไม่ได้ส่ง OTP
    // ผู้ใช้จึงต้องกด "ขอรหัส OTP" ได้ทันที (countdown = 0)
    // countdown 60 วินาทีจะเริ่มก็ต่อเมื่อขอ OTP สำเร็จใน resend2FA()
    this.$nextTick(() => {
      this.$refs.otpInput0[0].focus()
    })
  },

  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }
  },

  methods: {
    ...mapActions('auth', ['verify2FAAction', 'request2FAAction']),

    handleInput(index, event) {
      const value = event.target.value
      if (!/^\d*$/.test(value)) {
        this.otp[index] = ''
        return
      }

      if (value && index < 5) {
        this.$nextTick(() => {
          this.$refs[`otpInput${index + 1}`][0].focus()
        })
      }
    },

    handleKeydown(index, event) {
      if (
        event.key === 'Backspace' &&
        !this.otp[index] &&
        index > 0
      ) {
        this.$nextTick(() => {
          this.$refs[`otpInput${index - 1}`][0].focus()
        })
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        this.$refs[`otpInput${index - 1}`][0].focus()
      }

      if (event.key === 'ArrowRight' && index < 5) {
        this.$refs[`otpInput${index + 1}`][0].focus()
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      const digits = pastedData.replace(/\D/g, '').slice(0, 6)

      if (digits.length > 0) {
        for (let i = 0; i < 6; i++) {
          this.otp[i] = digits[i] || ''
        }

        this.$nextTick(() => {
          if (digits.length === 6) {
            this.$refs[`otpInput5`][0].blur()
          } else {
            this.$refs[`otpInput${digits.length}`][0].focus()
          }
        })
      }
    },

    async verify2FA() {
      const otpCode = this.otp.join('')
      if (otpCode.length !== 6) return

      this.isVerifying = true
      this.errorMessage = ''

      try {
        await this.verify2FAAction({
          phone: this.phone,
          otp: otpCode,
        })

        this.$toast?.success?.('ยืนยัน 2FA สำเร็จ')

        // Reload หน้าเดิมหลัง verify สำเร็จ เพื่อ:
        // 1) ล้าง overlay/scroll-lock ของ v-dialog ที่อาจค้างบน document
        //    (ไม่งั้นเปลี่ยนหน้าแล้วข้อมูลจะไม่แสดงจนกว่าจะ refresh เอง)
        // 2) โหลดข้อมูลของ request เดิมที่ถูก reject ตอนเจอ 401 2FA ใหม่
        window.location.reload()
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          'ยืนยัน 2FA ไม่สำเร็จ กรุณาลองใหม่'
        this.otp = ['', '', '', '', '', '']
        this.$refs.otpInput0[0].focus()
      } finally {
        this.isVerifying = false
      }
    },

    async resend2FA() {
      this.isResending = true
      this.errorMessage = ''

      try {
        await this.request2FAAction({
          phone: this.phone,
        })

        this.$toast?.success?.('ส่งรหัส OTP สำเร็จ')
        this.hasRequestedOtp = true
        this.otp = ['', '', '', '', '', '']
        this.resendCountdown = 60
        this.startResendCountdown()
        this.$refs.otpInput0[0].focus()
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message || 'ขอรหัส OTP ใหม่ไม่สำเร็จ'
      } finally {
        this.isResending = false
      }
    },

    startResendCountdown() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }

      this.resendTimer = setInterval(() => {
        this.resendCountdown--
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    },
  },
}
</script>

<style scoped lang="stylus">
.otp-2fa-content
  padding 16px 0

.otp-input-group
  display flex
  justify-content center
  gap 8px
  margin-bottom 20px

.otp-input
  width 45px
  height 50px
  padding 0
  text-align center
  font-size 24px
  font-weight 600
  border 2px solid #e0e0e0
  border-radius 8px
  outline none
  transition border-color 0.3s ease
  flex-shrink 0

  &:focus
    border-color #1976d2
    box-shadow 0 0 0 3px rgba(25, 118, 210, 0.1)

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button
    -webkit-appearance none
    margin 0

.resend-section
  text-align center
  padding-top 16px
  border-top 1px solid #f0f0f0

.countdown-info
  .countdown-text
    color #666
    font-size 14px
    line-height 1.5

    strong
      color #1976d2
      font-weight 600
</style>
